/* @flow */
import * as React from 'react';
import { useId } from 'react-aria';
import { isEmpty } from 'ramda';
import { useIntl } from 'react-intl';
import {
  homeRoute,
  profileEditRoute,
  userRoute,
} from 'src/shared/app/base/route/setting/routeSettings';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

// Styles
import styles from './BraindateParticipantsItemInterests.style';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

// Utils
import useSelf from 'src/shared/domain/user/hook/useSelf';
import messages from 'src/shared/app/braindate/l10n/braindateL10n';
import { getUserId } from '@braindate/domain/lib/user/util';
import { trackGAEvent } from 'src/shared/core/service/tracking/gaService';
import { getUserKeywords } from 'src/shared/app/user/util/userUtils';
import { userEditInterestsSectionId } from 'src/shared/components/domain/user/constants/userConstants';
import {
  getKeywordEnabled,
  getKeywordLabel,
} from 'src/shared/domain/keyword/util/keywordUtils';
import {
  invalidateMarket,
  setMarketQuery,
} from 'src/shared/app/market/action/marketActions';

// Hooks
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';

// Components
import TagGroup from 'src/shared/ui/component/button/Tag/TagGroup';
import TagButton from 'src/shared/ui/component/button/Tag/TagButton';
import LinkButton from 'src/shared/ui/component/link/LinkButton';
import { Skeleton } from '@mui/material';

// Types
import type { User } from '@braindate/domain/lib/user/type';
import { generatePath } from 'src/shared/app/base/route/util/routeUtils';

type Props = {
  participant: User,
  isFetching?: boolean,
  title?: string,
  onClose?: () => void,
};

const useStyles = createUseThemeStyles(styles);

const BraindateParticipantsItemInterests = ({
  participant,
  isFetching,
  title,
  onClose,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const classes = useStyles();
  const titleId = useId();
  const self = useSelf();

  const [homePath, profileEditPath] = useRoutes(homeRoute, profileEditRoute);

  const interestKeywords = getUserKeywords(participant, intl.locale) || [];
  const isSelf = !!self && getUserId(participant) === getUserId(self);

  const handleClick = (query: string) => {
    if (!isEmpty(query)) {
      trackGAEvent('Market search', 'Click suggested tag', query);

      dispatch(setMarketQuery(query));
      dispatch(invalidateMarket());

      if (onClose) {
        onClose();
      }

      navigate(homePath);
    }
  };

  return (
    <div className={classes.root}>
      {!!title && !!interestKeywords.length && (
        <p className={classes.title} id={titleId}>
          {title}
        </p>
      )}

      {isFetching ? (
        <div className={classes.skeletons}>
          {Array(3)
            .fill(0)
            .map((_, key) => (
              <Skeleton width={80} height={50} key={key} />
            ))}
        </div>
      ) : !interestKeywords.length ? (
        <div className={classes.emptyInterests}>
          <LinkButton
            to={
              isSelf
                ? `${profileEditPath}#${userEditInterestsSectionId}`
                : generatePath(userRoute, { id: getUserId(participant) })
            }
            onClick={() => {
              if (onClose) {
                onClose();
              }
            }}
          >
            {intl.formatMessage(
              isSelf ? messages.addInterests : messages.viewParticipantProfile,
            )}
          </LinkButton>
        </div>
      ) : (
        <TagGroup aria-describedby={titleId}>
          {interestKeywords.slice(0, 4).map((interest, key) => (
            <TagButton
              onClick={() => handleClick(getKeywordLabel(interest))}
              isInactive={!getKeywordEnabled(interest)}
              key={`interest-${key}`}
            >
              {getKeywordLabel(interest)}
            </TagButton>
          ))}
        </TagGroup>
      )}
    </div>
  );
};

export default React.memo<Props>(BraindateParticipantsItemInterests);
